import {
  createWorkshopRecordStore,
  type EntriesByWorkshop,
} from "@/lib/create-workshop-record-store";
import type { Workshop } from "@/content/workshops";
import { broadcastSync } from "@/lib/sync-broadcast";
import { isWorkshopBffEnabled } from "@/env";
import { deleteDoc, uploadDoc } from "@/lib/docs-api";
import {
  OVERRIDE_ID,
  sanitiseOverride,
  type DocUpload,
  type WorkshopOverride,
  type WorkshopOverridePatch,
} from "@/features/workshop/overrides/workshop-override-content";

/**
 * Per-workshop power-user content overrides store.
 *
 * Built on {@link createWorkshopRecordStore} so it inherits the BFF blob
 * plane + IndexedDB cache + BFF-first hydrate (with offline fallback) +
 * optimistic writes for free — the same durability posture as the
 * decisions/actions/assumptions stores. One SINGLETON record per workshop
 * (`id === OVERRIDE_ID`) holds every overridden field; the blob lands at
 * `workshops/<wsId>/records/workshop-overrides/overrides.json`, which the
 * BFF authorizes as read-for-everyone / write-for-Facilitator+Director
 * (`services/bff/app/policy.py`). No BFF code change — a new `recordType`
 * is just a new path prefix.
 *
 * Read sites resolve `override ?? staticDefault` via the resolvers in
 * `workshop-override-content.ts`; write sites go through the create-or-merge
 * helpers below. With no override set, every surface reads exactly what it
 * read before this store existed.
 */
/**
 * Recency-aware merge for `sync()`: each workshop holds a single override
 * record, so reconcile per workshop by keeping whichever side has the newer
 * `updatedAt`. This is last-write-wins across devices AND protects a fresh
 * local edit from a poll that lands before the BFF write propagates (a tie
 * keeps local). A workshop absent locally takes the BFF copy (so another
 * device's new override propagates here).
 */
function mergeByRecency(
  fresh: EntriesByWorkshop<WorkshopOverride>,
  local: EntriesByWorkshop<WorkshopOverride>,
): EntriesByWorkshop<WorkshopOverride> {
  const out = { ...fresh };
  for (const wsId of Object.keys(local) as Workshop["id"][]) {
    const f = (fresh[wsId] ?? []).find((r) => r.id === OVERRIDE_ID);
    const l = (local[wsId] ?? []).find((r) => r.id === OVERRIDE_ID);
    const winner = !l ? f : !f ? l : (l.updatedAt ?? 0) >= (f.updatedAt ?? 0) ? l : f;
    out[wsId] = winner ? [winner] : [];
  }
  return out;
}

export const useWorkshopOverrides = createWorkshopRecordStore<
  WorkshopOverride,
  WorkshopOverridePatch
>({
  idbKey: "xaes-workshop-overrides",
  recordType: "workshop-overrides",
  sanitiseItem: (raw, fallbackWorkshopId) => sanitiseOverride(raw, fallbackWorkshopId),
  mergeOnSync: mergeByRecency,
});

export { OVERRIDE_ID };

/** The singleton override for a workshop, if one has been written. */
function findOverride(workshopId: string): WorkshopOverride | undefined {
  const bucket =
    useWorkshopOverrides.getState().entriesByWorkshop[
      workshopId as Workshop["id"]
    ];
  return bucket?.find((r) => r.id === OVERRIDE_ID);
}

/** Imperative read (non-reactive) — for event handlers, not render. */
export function getWorkshopOverride(
  workshopId: string,
): WorkshopOverride | undefined {
  return findOverride(workshopId);
}

/**
 * Reactive selector hook — re-renders only when this workshop's override
 * record changes (the factory replaces the record object on mutation, so
 * `find` returns a stable reference between writes).
 */
export function useWorkshopOverride(
  workshopId: string,
): WorkshopOverride | undefined {
  return useWorkshopOverrides((s) =>
    s.entriesByWorkshop[workshopId as Workshop["id"]]?.find(
      (r) => r.id === OVERRIDE_ID,
    ),
  );
}

/** Create-or-merge the singleton override record for a workshop. Stamps
 *  `updatedAt` (for last-write-wins on sync) and pings other tabs. */
export function setWorkshopOverride(
  workshopId: Workshop["id"],
  patch: WorkshopOverridePatch,
): void {
  const store = useWorkshopOverrides.getState();
  const stamped: WorkshopOverridePatch = { ...patch, updatedAt: Date.now() };
  if (findOverride(workshopId)) {
    store.update(workshopId, OVERRIDE_ID, stamped);
  } else {
    store.add(workshopId, { id: OVERRIDE_ID, workshopId, ...stamped });
  }
  broadcastSync();
}

/**
 * Reset a single field to its static default. Sets the field to `undefined`
 * so it serialises away (JSON drops `undefined` keys) and resolvers fall
 * through — NOT `[]`, which an array field treats as a deliberate "empty".
 */
export function clearWorkshopOverrideField(
  workshopId: Workshop["id"],
  field: keyof WorkshopOverridePatch,
): void {
  if (!findOverride(workshopId)) return;
  useWorkshopOverrides
    .getState()
    .update(workshopId, OVERRIDE_ID, {
      [field]: undefined,
      updatedAt: Date.now(),
    } as WorkshopOverridePatch);
  broadcastSync();
}

/** Reset every field for a workshop to its static default. */
export function clearWorkshopOverrides(workshopId: Workshop["id"]): void {
  useWorkshopOverrides.getState().remove(workshopId, OVERRIDE_ID);
  broadcastSync();
}

/* ── Pre-reading document upload ──────────────────────────────────────────
 * The bytes live as a blob on the BFF `workshops` tier (read-for-all /
 * write-for-Facilitator+Director); only the reference rides in the override
 * record. Requires the BFF backend — there's no IDB fallback for a SHARED
 * binary (base64-in-JSON would bloat the synced record). */

const WORKSHOPS_TIER = "workshops";

/** Strip any path and unsafe characters from a chosen filename. */
function safeFilename(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? name;
  return base.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 120) || "document";
}

/**
 * Upload a file to the BFF `workshops` tier under `<wsId>/<folder>/<name>`
 * and return a {@link DocUpload} reference for the caller to store in an
 * override field. Returns null when no BFF backend is configured (a shared
 * binary has no IDB fallback); throws on a transient upload/auth failure.
 */
export async function uploadWorkshopFile(
  workshopId: Workshop["id"],
  folder: string,
  file: File,
): Promise<DocUpload | null> {
  if (!isWorkshopBffEnabled()) return null;
  const path = `${workshopId}/${folder}/${safeFilename(file.name)}`;
  await uploadDoc(WORKSHOPS_TIER, path, file);
  return {
    path,
    filename: file.name,
    contentType: file.type || undefined,
    size: file.size,
    uploadedAt: Date.now(),
  };
}

/**
 * Upload a pre-reading document for a workshop and point the override at it.
 * Returns false when the BFF backend isn't configured.
 *
 * Writes through the effective override path so default-mode clients store
 * the reference on the shared program defaults, not the client blob.
 */
export async function uploadPreReading(
  workshopId: Workshop["id"],
  file: File,
): Promise<boolean> {
  const { getEffectiveWorkshopOverride, setEffectiveOverride } = await import(
    "@/stores/useEffectiveWorkshopOverride"
  );
  const prev = getEffectiveWorkshopOverride(workshopId)?.docUpload;
  const upload = await uploadWorkshopFile(workshopId, "prereading", file);
  if (!upload) return false;
  // Best-effort clean-up of a previous upload at a different path.
  if (prev && prev.path !== upload.path) {
    void deleteDoc(WORKSHOPS_TIER, prev.path).catch(() => {});
  }
  setEffectiveOverride(workshopId, { docUpload: upload });
  return true;
}

/** Clear the uploaded pre-reading doc (falls back to authored/ default
 *  sections) and best-effort delete the blob. */
export function clearPreReading(workshopId: Workshop["id"]): void {
  // Lazy import avoids a cycle: effective helpers → this module.
  void import("@/stores/useEffectiveWorkshopOverride").then(
    ({ getEffectiveWorkshopOverride, clearEffectiveOverrideField }) => {
      const prev = getEffectiveWorkshopOverride(workshopId)?.docUpload;
      clearEffectiveOverrideField(workshopId, "docUpload");
      if (prev && isWorkshopBffEnabled()) {
        void deleteDoc(WORKSHOPS_TIER, prev.path).catch(() => {});
      }
    },
  );
}
